import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { getOrganizationSubscriptionAndTrial } from '@eventbrite/pricing-and-packaging-api';
import {
    CORE_MARKETING_AUTOMATION_API_KEY_CONFIG,
    useFeatureFlagState,
} from '@eventbrite/splitio-checker';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { Illustration } from '../../assets/Illustration';
import { CTA_BUTTON_TEXT, DISMISS_BUTTON_TEXT } from '../../constants';
import { HeapActionProps, SalesEligibleBannerProps } from '../../types';
import {
    checkLastInteraction,
    getCohortContent,
    getCohortData,
    isNarrow,
    isOpportunityColor,
    shouldShowBannerFromCohort,
    trackHeapEvent,
} from '../../utils';
import { SalesEligibleModal } from '../SalesEligibleModal/SalesEligibleModal';
import { CTAButton, Description, DismissButton, Title } from './components';
import './SalesEligibleBanner.scss';

const getSalesEligibleDismissed = () =>
    localStorage.getItem('salesEligibleDismissed') === 'true';

const getSalesEligibleCompleted = () =>
    localStorage.getItem('salesEligibleCompleted') === 'true';

const setSalesEligibleLastInteraction = () => {
    localStorage.setItem('salesEligibleLastInteraction', Date.now().toString());
    localStorage.setItem('salesEligibleDismissed', 'true');
};

export const SalesEligibleBanner: React.FC<SalesEligibleBannerProps> = ({
    entryPoint,
    updateUserContextFromSalesEligible,
}) => {
    const isBannerEnabled = useFeatureFlagState(
        'show_sales_eligible_onboarding_banner',
        CORE_MARKETING_AUTOMATION_API_KEY_CONFIG,
    );

    const isEnglishSpeakingLocale = navigator?.language?.startsWith('en');
    const activeOrgId = HAS_WINDOW
        ? window.__SERVER_DATA__?.activeOrganizationId ?? ''
        : '';

    const [showBanner, setShowBanner] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const { data: cohort } = useQuery(
        ['sales-eligible'],
        () => getCohortData(entryPoint),
        {
            enabled: !!isBannerEnabled,
            staleTime: Infinity,
            onSuccess: (data) => {
                if (data) {
                    setShowBanner(checkLastInteraction());
                }
            },
        },
    );

    const { data: subscriptionData } = useQuery(
        ['organization-subscription', activeOrgId],
        () => getOrganizationSubscriptionAndTrial(activeOrgId),
        {
            enabled: !!isBannerEnabled && !!activeOrgId,
            staleTime: Infinity,
        },
    );

    const isOrgOnContract =
        subscriptionData?.subAndTrialStatus?.is_subscribed &&
        subscriptionData?.subscription?.plan_tier === 'ADMIN_PRO';

    const initialDismissed = useRef(getSalesEligibleDismissed());
    const initialCompleted = useRef(getSalesEligibleCompleted());

    const shouldRenderBanner =
        isBannerEnabled &&
        isEnglishSpeakingLocale &&
        !initialCompleted.current &&
        !initialDismissed.current &&
        !isOrgOnContract &&
        showBanner;

    useEffect(() => {
        if (
            cohort &&
            shouldRenderBanner &&
            shouldShowBannerFromCohort(cohort, entryPoint)
        ) {
            trackHeapEvent(
                HeapActionProps.SalesEligibleBannerShown,
                entryPoint,
                cohort,
            );
        }
    }, [cohort, entryPoint, shouldRenderBanner]);

    if (
        !cohort ||
        !shouldRenderBanner ||
        !shouldShowBannerFromCohort(cohort, entryPoint)
    ) {
        return null;
    }

    const { title: titleText, description: descriptionText } =
        getCohortContent(cohort);

    const handleNotNowClick = () => {
        setShowBanner(false);
        setSalesEligibleLastInteraction();
    };

    const openModal = () => {
        trackHeapEvent(
            HeapActionProps.SalesEligibleBannerClicked,
            entryPoint,
            cohort,
        );
        setShowModal(true);
    };

    return (
        <section
            aria-label="Sales Eligible Banner"
            className={`sales-eligible-banner__container ${
                isOpportunityColor(entryPoint) ? 'opportunity' : ''
            } ${isNarrow(entryPoint) ? 'narrow' : ''}`}
            data-testid="sales-eligible-banner"
        >
            <div className="sales-eligible-banner__image">
                <Illustration />
            </div>
            <div className="sales-eligible-banner__content">
                <Title title={titleText} />
                <Description description={descriptionText} />
                <div className="sales-eligible-banner__button-container">
                    <CTAButton
                        buttonText={CTA_BUTTON_TEXT}
                        handleButtonClick={openModal}
                        isNarrow={isNarrow(entryPoint)}
                    />
                    <DismissButton
                        buttonText={DISMISS_BUTTON_TEXT}
                        handleButtonClick={handleNotNowClick}
                        isNarrow={isNarrow(entryPoint)}
                    />
                </div>
            </div>
            {showModal && (
                <SalesEligibleModal
                    cohort={cohort}
                    entryPoint={entryPoint}
                    handleClose={() => setShowModal(false)}
                    handleComplete={() => {
                        setShowModal(false);
                        setShowBanner(false);
                    }}
                    updateUserContextFromSalesEligible={
                        updateUserContextFromSalesEligible
                    }
                />
            )}
        </section>
    );
};
